// Implements: F-SHR-003, F-SHR-005, F-SHR-006, F-SHR-008, F-SHR-009
/**
 * The `sharing` module's client for the management routes of SA9, on top of
 * the single HTTP exit.
 *
 * Create, revoke and edit are sent with `retry: false` (checklist 13). None is
 * a paid call, but none may be repeated behind the person's back: a create
 * retried after a lost answer would mint a second link (and a second URL the
 * person never sees) and spend one of the mockup's active-link slots; a revoke
 * and an edit are the person's decisions, made once.
 *
 * **A listed link keeps only the fields the client shows** (`toShareLink`,
 * `toPanelLink`, SB5.5): no list has a field for the token, its hash or a URL,
 * and if a server ever sent one it would be dropped here, before the answer
 * reaches the query cache or the DOM.
 *
 * Nothing here logs, and the creation answer is handed back to the caller
 * only: the URL it carries is the one value the client must not keep.
 */

import { ApiError, UNKNOWN_ERROR_CODE } from "../../../shared/api/ApiError";
import { API_BASE } from "../../../shared/api/apiBase";
import { httpRequest } from "../../../shared/api/httpClient";
import type { VersionTreeNode, VersionTreeView } from "../../../types/api";
import type {
  CreateShareLinkInput,
  CreatedShareLinkView,
  EditShareLinkInput,
  MyLinksPageView,
  PanelLinkView,
  ShareLinkListView,
  ShareLinkView,
} from "../../../types/sharing";

/** The person's own links across mockups (SB3, P6-OD-13). */
export const MY_LINKS_PATH = `${API_BASE}/sharing/links`;

/** A listed link, field by field: whatever else the payload carries is not kept. */
export function toShareLink(raw: ShareLinkView): ShareLinkView {
  return {
    id: raw.id,
    mockup_id: raw.mockup_id,
    artifact: raw.artifact,
    mode: raw.mode,
    frozen_version_id: raw.frozen_version_id,
    label: raw.label,
    created_by: raw.created_by,
    created_at: raw.created_at,
    expires_at: raw.expires_at,
    revoked_at: raw.revoked_at,
    state: raw.state,
    has_password: raw.has_password,
    may_revoke: raw.may_revoke,
  };
}

/** A link of the panel, field by field, like `toShareLink`. */
export function toPanelLink(raw: PanelLinkView): PanelLinkView {
  return {
    ...toShareLink(raw),
    mockup_name: raw.mockup_name,
    total_opens: raw.total_opens,
    last_opened_at: raw.last_opened_at,
  };
}

export function shareLinksPath(mockupId: string): string {
  return `${API_BASE}/mockups/${encodeURIComponent(mockupId)}/share-links`;
}

export async function fetchShareLinks(mockupId: string): Promise<readonly ShareLinkView[]> {
  const body = await httpRequest<Partial<ShareLinkListView>>(shareLinksPath(mockupId));
  return (body?.links ?? []).map(toShareLink);
}

/** One page of my links; `cursor` is the previous page's `next_cursor`, absent for the first. */
export async function fetchMyLinks(cursor: string | undefined): Promise<MyLinksPageView> {
  const query = cursor === undefined ? "" : `?cursor=${encodeURIComponent(cursor)}`;
  const body = await httpRequest<Partial<MyLinksPageView>>(`${MY_LINKS_PATH}${query}`);
  return { links: (body?.links ?? []).map(toPanelLink), next_cursor: body?.next_cursor ?? null };
}

/** Change the expiry, the password and/or the label of one link; answers the link as the lists show it. */
export async function editShareLink(
  mockupId: string,
  linkId: string,
  input: EditShareLinkInput,
): Promise<ShareLinkView> {
  const body = await httpRequest<ShareLinkView>(
    `${shareLinksPath(mockupId)}/${encodeURIComponent(linkId)}`,
    { method: "PATCH", body: input, retry: false },
  );
  if (body === undefined) {
    throw new ApiError(200, UNKNOWN_ERROR_CODE, "PATCH .../share-links/{id} answered with no body", new Headers());
  }
  return toShareLink(body);
}

export async function createShareLink(
  mockupId: string,
  input: CreateShareLinkInput,
): Promise<CreatedShareLinkView> {
  const body = await httpRequest<CreatedShareLinkView>(shareLinksPath(mockupId), {
    method: "POST",
    body: input,
    retry: false,
  });
  if (body === undefined) {
    throw new ApiError(201, UNKNOWN_ERROR_CODE, "POST .../share-links answered with no body", new Headers());
  }
  return body;
}

export async function revokeShareLink(mockupId: string, linkId: string): Promise<void> {
  await httpRequest<unknown>(`${shareLinksPath(mockupId)}/${encodeURIComponent(linkId)}`, {
    method: "DELETE",
    retry: false,
  });
}

/**
 * The version nodes a frozen link may point at: the same route and the same
 * answer as the `versions` module's tree, read under the same shared key
 * (`versionTreeKey`), so the picker costs no second request when the tree is
 * already on the page. A module may not import another, hence this copy.
 */
export async function fetchShareableVersions(mockupId: string): Promise<readonly VersionTreeNode[]> {
  const tree = await httpRequest<Partial<VersionTreeView>>(
    `${API_BASE}/mockups/${encodeURIComponent(mockupId)}/versions/tree`,
  );
  return tree?.nodes ?? [];
}
